import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Company } from './company.model';
import { TenantAccessService } from './tenant-access.service';
import {
    CompanyAccess,
    CompanyGroupAccess,
    RouteTenantScope,
    ScopedRole,
    TenantAccessSummary,
    TenantScope
} from './tenant-access.model';
import { SessionStorageService } from '../services/session-storage.service';

@Injectable({ providedIn: 'root' })
export class TenantScopeService {
    private readonly summarySubject = new BehaviorSubject<TenantAccessSummary | null>(null);
    private readonly activeScopeSubject = new BehaviorSubject<TenantScope | null>(null);

    readonly accessSummary$ = this.summarySubject.asObservable();
    readonly activeScope$ = this.activeScopeSubject.asObservable();

    constructor(
        private tenantAccessService: TenantAccessService,
        private sessionStorageService: SessionStorageService
    ) {
        this.summarySubject.next(this.sessionStorageService.getTenantAccessSummary() ?? null);
        this.activeScopeSubject.next(this.sessionStorageService.getActiveTenantScope() ?? null);
    }

    initializeCurrentAccess(): Observable<TenantAccessSummary | null> {
        return this.tenantAccessService.getCurrentAccess().pipe(
            tap(summary => this.applyAccessSummary(summary)),
            catchError(() => {
                this.restoreLegacyCompanyScope();
                return of(null);
            })
        );
    }

    applyAccessSummary(summary: TenantAccessSummary): void {
        const normalized: TenantAccessSummary = {
            ...summary,
            companies: summary.companies ?? [],
            companyGroups: summary.companyGroups ?? []
        };
        this.summarySubject.next(normalized);
        this.sessionStorageService.saveTenantAccessSummary(normalized);
        this.ensureActiveScope(normalized);
    }

    getAccessSummary(): TenantAccessSummary | null {
        return this.summarySubject.value;
    }

    getActiveScope(): TenantScope | null {
        return this.activeScopeSubject.value;
    }

    selectCompany(companyId: number): boolean {
        const access = this.findActiveCompany(companyId);
        if (!access) {
            return false;
        }
        this.setActiveScope(this.toCompanyScope(access));
        const currentCompany = this.sessionStorageService.getCompany();
        this.sessionStorageService.saveCompany({
            ...(currentCompany?.id === access.companyId ? currentCompany : {}),
            id: access.companyId,
            name: access.companyName
        } as Company);
        return true;
    }

    selectCompanyGroup(companyGroupId: number): boolean {
        const access = this.findActiveCompanyGroup(companyGroupId);
        if (!access) {
            return false;
        }
        this.setActiveScope(this.toGroupScope(access));
        return true;
    }

    routeScopeAllowed(requiredScope?: RouteTenantScope): boolean {
        const scope = this.getActiveScope();
        if (!scope) {
            return !requiredScope || requiredScope === 'COMPANY' || requiredScope === 'ANY';
        }
        if (!requiredScope || requiredScope === 'COMPANY') {
            return scope?.type === 'COMPANY';
        }
        if (requiredScope === 'GROUP') {
            return scope?.type === 'GROUP';
        }
        return !!scope;
    }

    requireCompanyScope(): CompanyAccess | null {
        const scope = this.getActiveScope();
        if (scope?.type !== 'COMPANY') {
            return null;
        }
        return this.findActiveCompany(scope.companyId) ?? null;
    }

    isGroupScope(): boolean {
        return this.getActiveScope()?.type === 'GROUP';
    }

    getActiveRoles(): ScopedRole[] {
        return this.getActiveScope()?.roles ?? [];
    }

    getActivePermissionCodes(): Set<string> {
        const permissions = new Set<string>();
        for (const role of this.getActiveRoles()) {
            for (const permissionCode of role.permissionCodes ?? []) {
                if (permissionCode?.trim()) {
                    permissions.add(permissionCode);
                }
            }
        }
        return permissions;
    }

    getAccessibleCompanies(): CompanyAccess[] {
        return (this.getAccessSummary()?.companies ?? []).filter(company => company.active !== false);
    }

    getAccessibleCompanyGroups(): CompanyGroupAccess[] {
        return (this.getAccessSummary()?.companyGroups ?? []).filter(group => group.active !== false);
    }

    refreshSelectedCompanyDetails(company: Company): void {
        const active = this.getActiveScope();
        if (active?.type === 'COMPANY' && active.companyId === company.id) {
            this.sessionStorageService.saveCompany(company);
            this.setActiveScope({
                ...active,
                companyName: company.name ?? active.companyName
            });
        }
    }

    private ensureActiveScope(summary: TenantAccessSummary): void {
        const saved = this.sessionStorageService.getActiveTenantScope();
        if (saved?.type === 'COMPANY' && this.findActiveCompany(saved.companyId, summary)) {
            this.selectCompany(saved.companyId);
            return;
        }
        if (saved?.type === 'GROUP' && this.findActiveCompanyGroup(saved.companyGroupId, summary)) {
            this.selectCompanyGroup(saved.companyGroupId);
            return;
        }

        const currentCompanyId = this.sessionStorageService.getCompany()?.id;
        if (currentCompanyId && this.findActiveCompany(currentCompanyId, summary)) {
            this.selectCompany(currentCompanyId);
            return;
        }

        const firstCompany = summary.companies?.find(company => company.active !== false);
        if (firstCompany) {
            this.selectCompany(firstCompany.companyId);
            return;
        }

        const firstGroup = summary.companyGroups?.find(group => group.active !== false);
        if (firstGroup) {
            this.selectCompanyGroup(firstGroup.companyGroupId);
            return;
        }

        this.setActiveScope(null);
    }

    private restoreLegacyCompanyScope(): void {
        const company = this.sessionStorageService.getCompany();
        if (!company?.id) {
            return;
        }
        this.setActiveScope({
            type: 'COMPANY',
            companyId: company.id,
            companyName: company.name ?? 'Current company',
            roles: [],
            legacyFallback: true
        });
    }

    private findActiveCompany(companyId: number, summary: TenantAccessSummary | null = this.getAccessSummary()): CompanyAccess | undefined {
        return (summary?.companies ?? []).find(company => company.companyId === companyId && company.active !== false);
    }

    private findActiveCompanyGroup(companyGroupId: number, summary: TenantAccessSummary | null = this.getAccessSummary()): CompanyGroupAccess | undefined {
        return (summary?.companyGroups ?? []).find(group => group.companyGroupId === companyGroupId && group.active !== false);
    }

    private toCompanyScope(access: CompanyAccess): TenantScope {
        return {
            type: 'COMPANY',
            companyId: access.companyId,
            companyName: access.companyName,
            roles: access.roles ?? [],
            legacyFallback: !!access.legacyFallback
        };
    }

    private toGroupScope(access: CompanyGroupAccess): TenantScope {
        return {
            type: 'GROUP',
            companyGroupId: access.companyGroupId,
            companyGroupName: access.companyGroupName,
            memberCompanies: access.memberCompanies ?? [],
            roles: access.roles ?? []
        };
    }

    private setActiveScope(scope: TenantScope | null): void {
        this.activeScopeSubject.next(scope);
        if (scope) {
            this.sessionStorageService.saveActiveTenantScope(scope);
        } else {
            this.sessionStorageService.clearActiveTenantScope();
        }
    }
}
